import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { i18next, dialogs, site, view, integration } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import itemsAddedCount from '../../../common/helpers/items-added-count';
import itemsDeletedCount from '../../../common/helpers/items-deleted-count';
import ContactFormSettingsDialog from '../components/contact-form-settings-dialog';
import constants from '../constants';
import verifiers from '../verifiers';
import helpers from '../helpers';

const { useForm } = reactHookForm;

const {
  track,
  constants: { events, triggerIds },
  helpers: { getDefaultTraits },
} = segment;
const {
  common: { MODAL_HEIGHT, MODAL_HEIGHT_WITH_TABS, MODAL_WIDTH },
  attributes,
  tabIdentifiers,
} = constants;

const getModalPosition = (element) => {
  const modalHeight = verifiers.isBuilderEnabled() ? MODAL_HEIGHT_WITH_TABS : MODAL_HEIGHT;
  const { offsetX, offsetY } = dialogs.helpers.getPositionByElement(
    element,
    MODAL_WIDTH,
    modalHeight
  );

  return {
    offsetX,
    offsetY,
  };
};

const getInitialEmails = (elementId) => {
  const element = view.accessors.getLiveElement(elementId);
  const hasCustomEmails =
    element.hasAttribute(attributes.EMAILS) && element.getAttribute(attributes.EMAILS).trim();

  if (hasCustomEmails) {
    const customEmails = view.accessors
      .getElementAttribute(elementId, attributes.EMAILS)
      .split(',');

    return customEmails.map((email) => ({
      value: email.trim(),
      label: email.trim(),
    }));
  }

  const { email: userEmail, is_white_label: isWL } = integration.accessors.getUserData();
  const businessEmails = isWL ? [] : site.accessors.getUserEmails() || [userEmail];

  return businessEmails.map((email) => ({
    value: email,
    label: email,
  }));
};

const getInitialSuccessMessage = (elementId) =>
  view.accessors.getElementAttribute(elementId, constants.attributes.SUCCESS_MESSAGE);

function ContactFormSettingsContainer({
  elementId,
  fields: fieldsProp = null,
  formState: formStateProp = null,
  modalPosition: modalPositionProp = null,
  onDialogMainAction,
  onDialogCancel,
}) {
  const element = view.accessors.getLiveElement(elementId);
  const initialFields = helpers.getFields(elementId);
  const initialGdprValue = verifiers.isGdprCheckboxEnabled(elementId);
  const isGdprEnabled = initialGdprValue !== null;
  const initialModalPosition = modalPositionProp || getModalPosition(element);

  const [fields, updateFields] = useState(fieldsProp || initialFields);
  const [isOrderAdjusted, setIsOrderAdjusted] = useState(false);
  const [isOverlayClicked, setIsOverlayClicked] = useState(false);
  const [modalPosition, setModalPosition] = useState(initialModalPosition);
  const [activeTabId, setActiveTabId] = useState(tabIdentifiers.GENERAL);

  const initialEmails = formStateProp?.emails || getInitialEmails(elementId) || [];
  const initialSuccessMessage =
    formStateProp?.successMessage ||
    getInitialSuccessMessage(elementId) ||
    i18next.t('Your message was successfully sent.');
  const initialGdpr = formStateProp?.gdpr || initialGdprValue;

  const {
    control,
    getValues,
    formState: { isValid },
    trigger: triggerValidation,
  } = useForm({
    mode: 'all',
    defaultValues: {
      emails: initialEmails,
      successMessage: initialSuccessMessage,
      ...(isGdprEnabled && {
        gdpr: initialGdpr,
      }),
    },
  });

  const showContactFormSettingsDialog = (data = {}) => {
    dialogs.operations.show(dialogTypes.CONTACT_FORM_SETTINGS_DIALOG, {
      elementId,
      fields,
      formState: getValues(),
      modalPosition,
      ...data,
    });
  };

  const handleFormSettingsSubmit = () => {
    if (!isValid) {
      triggerValidation();

      if (activeTabId === tabIdentifiers.GENERAL) {
        setActiveTabId(tabIdentifiers.ADVANCED);
      }

      return;
    }

    const values = getValues();
    const staticElement = view.accessors.getStaticElement(elementId).cloneNode(true);
    const customEmails = view.accessors.getElementAttribute(elementId, attributes.EMAILS);

    onDialogMainAction({
      dialogType: dialogTypes.CONTACT_FORM_SETTINGS_DIALOG,
      ...getDefaultTraits(elementId),
      triggerId: isOverlayClicked ? triggerIds.DIALOG_BACKGROUND : triggerIds.SUBMIT_BUTTON,
      itemsAdded: itemsAddedCount(initialFields, fields),
      itemsDeleted: itemsDeletedCount(initialFields, fields),
      itemsOrderAdjusted: isOrderAdjusted,
      itemsEdited: helpers.getItemsEditedCount(initialFields, fields),
      itemsCount: fields.length,
      displayGdprAdjusted: initialGdprValue !== values.gdpr,
      isDisplayGdprTurnedOn: isGdprEnabled ? values.gdpr : null,
    });

    if (verifiers.isBuilderEnabled()) {
      helpers.buildNewMarkup(staticElement, fields);
    }

    helpers.setDerivedAttributes(staticElement, {
      customEmails,
      ...values,
    });

    setIsOverlayClicked(false);
    view.operations.replaceElement(staticElement, elementId);
    dialogs.operations.hide();
  };

  const handleFormSettingsCancel = () => {
    onDialogCancel({
      dialogType: dialogTypes.CONTACT_FORM_SETTINGS_DIALOG,
      ...getDefaultTraits(elementId),
    });

    dialogs.operations.hide();
  };

  const handleChangeFieldOrder = (data) => {
    track(events.CONTACT_FORM_ITEMS_ORDER_CHANGED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.CONTACT_FORM_SETTINGS_DIALOG,
    });

    setIsOrderAdjusted(true);
    updateFields(data);
  };

  const handleAddField = () => {
    dialogs.operations.show(dialogTypes.CONTACT_FORM_SELECT_ITEM_DIALOG, {
      elementId,
      fields,
      formState: getValues(),
      modalPosition,
    });
  };

  const handleEditField = (id) => {
    dialogs.operations.show(dialogTypes.CONTACT_FORM_EDIT_FIELD_DIALOG, {
      elementId,
      field: helpers.getItemById(fields, id),
      fields,
      formState: getValues(),
      modalPosition,
      onSubmit: (data) => {
        const updatedFields = fields.map((field) => {
          if (field.id === id) {
            return data.field;
          }

          return field;
        });

        showContactFormSettingsDialog({
          fields: updatedFields,
          modalPosition: data.modalPosition,
        });
      },
      onCancel: (data) => {
        showContactFormSettingsDialog({
          modalPosition: data.modalPosition,
        });
      },
    });
  };

  const handleDeleteField = ({ id, type }) => {
    const initialField = helpers.getItemById(initialFields, id);
    const filterOutDeletedField = () => fields.filter((field) => field.id !== id);

    if (type === 'email') {
      const emailFieldsCount = fields.filter((field) => field.type === 'email').length;

      if (emailFieldsCount === 1) {
        dialogs.operations.show(dialogTypes.CONTACT_FORM_DELETE_EMAIL_DIALOG, {
          elementId,
          onSubmit: () => {
            showContactFormSettingsDialog({
              fields: filterOutDeletedField(),
            });
          },
          onCancel: () => {
            showContactFormSettingsDialog();
          },
        });
      } else {
        updateFields(filterOutDeletedField());
      }
    } else {
      updateFields(filterOutDeletedField());
    }

    track(events.CONTACT_FORM_DELETE_ITEM_BUTTON_CLICKED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.CONTACT_FORM_SETTINGS_DIALOG,
      itemId: id,
      itemCurrentLabel: initialField.label || null,
      itemType: type,
    });
  };

  const handleGdprFieldChange = () => {
    track(events.CONTACT_FORM_DISPLAY_GDPR_CHECKBOX_CLICKED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.CONTACT_FORM_SETTINGS_DIALOG,
      action: getValues('gdpr') ? 'turn-on' : 'turn-off',
    });
  };

  const handleModalOverlayClick = () => {
    setIsOverlayClicked(true);
  };

  const handleModalPositionUpdate = (event, { x, y }) => {
    setModalPosition({ offsetX: x, offsetY: y });
  };

  return (
    <ContactFormSettingsDialog
      control={control}
      fields={fields}
      modalPosition={modalPosition}
      activeTabId={activeTabId}
      isGdprEnabled={isGdprEnabled}
      onTabChange={setActiveTabId}
      onSubmit={handleFormSettingsSubmit}
      onCancel={handleFormSettingsCancel}
      onAddField={handleAddField}
      onEditField={handleEditField}
      onChangeFieldOrder={handleChangeFieldOrder}
      onDeleteField={handleDeleteField}
      onGdprChange={handleGdprFieldChange}
      onModalOverlayClick={handleModalOverlayClick}
      onModalPositionUpdate={handleModalPositionUpdate}
    />
  );
}

ContactFormSettingsContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  formState: PropTypes.shape(),
  modalPosition: PropTypes.shape({
    offsetX: PropTypes.number.isRequired,
    offsetY: PropTypes.number.isRequired,
  }),
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default ContactFormSettingsContainer;
